import { useState } from "react";
import { ChevronDown } from "lucide-react";

const FAQ_BY_LANG = {
  es: {
    title: "Preguntas frecuentes",
    items: [
      {
        q: "¿Qué pasa con mis datos? ¿Quién los ve?",
        a: "Tus datos viven en una base PostgreSQL privada que solo tu cuenta puede consultar. No los compartimos con terceros, no entrenamos modelos con ellos y no aparecen en analíticas externas con identificación individual. Si cancelas tu cuenta, eliminamos todo a pedido.",
      },
      {
        q: "¿Cuánto demora importar archivos por primera vez?",
        a: "El primer onboarding toma menos de 5 minutos. Subes tu reporte de Dropi y tu resumen de Meta Ads (formatos Excel estándar de cada plataforma) y Zenviq los cruza automáticamente. No necesitas instalar nada ni dar permisos OAuth.",
      },
      {
        q: "¿Funciona si vendo en Shopify además de Dropi?",
        a: "Hoy Zenviq está optimizado para operación con Dropi en pago contra entrega. Si vendes mayoritariamente en Shopify con checkout online, las métricas de tasa de confirmación y entrega no aplican igual. Estamos evaluando soporte Shopify para fines de 2026.",
      },
      {
        q: "¿Puedo cancelar el plan Pro en cualquier momento?",
        a: "Sí. Cancelas desde tu cuenta Hotmart en cualquier momento y conservas el acceso hasta el final del período pagado. No hay cargos automáticos sorpresa ni contratos mínimos.",
      },
      {
        q: "¿Cómo cruzan los datos de Meta y Dropi sin acceso directo a mis cuentas?",
        a: "No necesitamos acceso directo. Tú exportas dos archivos: el reporte de Dropi (todos tus pedidos) y el resumen de Meta Ads Manager (gasto por campaña). Zenviq usa fuzzy matching para asociar cada campaña con sus pedidos correspondientes y calcular el costo real por pedido pagado.",
      },
      {
        q: "¿Hay garantía de reembolso?",
        a: "Sí. Tienes 14 días desde tu primer pago para pedir reembolso completo, sin necesidad de explicar por qué. Lo gestionas directamente desde Hotmart.",
      },
      {
        q: "¿Aparecen en Zenviq mis ventas en USD si vendo en COP/MXN?",
        a: "Sí. La calculadora de precio mínimo soporta CLP, ARS, COP, MXN, PEN, BRL, UYU y USD. Las vistas de rentabilidad muestran los montos en la moneda de tu cuenta Dropi. Pronto agregaremos conversión automática para comparaciones cross-país.",
      },
    ],
  },
  pt: {
    title: "Perguntas frequentes",
    items: [
      {
        q: "O que acontece com meus dados? Quem os vê?",
        a: "Seus dados ficam em um banco PostgreSQL privado que somente sua conta pode consultar. Não compartilhamos com terceiros, não treinamos modelos com eles e não aparecem em analíticas externas com identificação individual. Se você cancela sua conta, apagamos tudo a pedido.",
      },
      {
        q: "Quanto demora importar arquivos pela primeira vez?",
        a: "O onboarding inicial leva menos de 5 minutos. Você sobe seu relatório do Dropi e o resumo do Meta Ads e o Zenviq os cruza automaticamente. Não precisa instalar nada nem dar permissões OAuth.",
      },
      {
        q: "Funciona se eu vender no Shopify além do Dropi?",
        a: "Hoje o Zenviq está otimizado para operação com Dropi em pagamento na entrega. Se você vende majoritariamente no Shopify com checkout online, as métricas de taxa de confirmação e entrega não se aplicam igualmente. Estamos avaliando suporte ao Shopify para o final de 2026.",
      },
      {
        q: "Posso cancelar o plano Pro a qualquer momento?",
        a: "Sim. Você cancela pela sua conta Hotmart a qualquer momento e mantém o acesso até o fim do período pago. Não há cobranças automáticas surpresa nem contratos mínimos.",
      },
      {
        q: "Como vocês cruzam os dados do Meta e do Dropi sem acesso direto às minhas contas?",
        a: "Não precisamos de acesso direto. Você exporta dois arquivos: o relatório do Dropi (todos os seus pedidos) e o resumo do Meta Ads Manager (gasto por campanha). O Zenviq usa fuzzy matching para associar cada campanha com seus pedidos e calcular o custo real por pedido pago.",
      },
      {
        q: "Há garantia de reembolso?",
        a: "Sim. Você tem 14 dias desde o primeiro pagamento para pedir reembolso completo, sem precisar explicar o porquê. Gestiona-se diretamente pelo Hotmart.",
      },
      {
        q: "Minhas vendas em USD aparecem no Zenviq se eu vendo em COP/MXN?",
        a: "Sim. A calculadora de preço mínimo suporta CLP, ARS, COP, MXN, PEN, BRL, UYU e USD. As visões de rentabilidade mostram os valores na moeda da sua conta Dropi. Em breve adicionaremos conversão automática para comparações entre países.",
      },
    ],
  },
  en: {
    title: "Frequently asked questions",
    items: [
      {
        q: "What happens to my data? Who can see it?",
        a: "Your data lives in a private PostgreSQL database that only your account can query. We don't share it with third parties, we don't train models with it, and it doesn't appear in external analytics with individual identification. If you cancel, we delete everything on request.",
      },
      {
        q: "How long does it take to import files for the first time?",
        a: "Initial onboarding takes less than 5 minutes. You upload your Dropi report and your Meta Ads summary (standard Excel formats) and Zenviq cross-references them automatically. No installation, no OAuth permissions required.",
      },
      {
        q: "Does it work if I sell on Shopify in addition to Dropi?",
        a: "Today Zenviq is optimized for cash-on-delivery operation with Dropi. If you sell mostly on Shopify with online checkout, confirmation and delivery rates don't apply the same way. We're evaluating Shopify support for late 2026.",
      },
      {
        q: "Can I cancel the Pro plan at any time?",
        a: "Yes. You cancel from your Hotmart account at any time and keep access until the end of the paid period. No surprise auto-charges, no minimum contracts.",
      },
      {
        q: "How do you cross-reference Meta and Dropi data without direct access to my accounts?",
        a: "We don't need direct access. You export two files: the Dropi report (all your orders) and the Meta Ads Manager summary (spend per campaign). Zenviq uses fuzzy matching to associate each campaign with its orders and calculate the real cost per paid order.",
      },
      {
        q: "Is there a refund guarantee?",
        a: "Yes. You have 14 days from your first payment to request a full refund, no questions asked. Managed directly from Hotmart.",
      },
      {
        q: "Do my USD sales show in Zenviq if I sell in COP/MXN?",
        a: "Yes. The minimum-price calculator supports CLP, ARS, COP, MXN, PEN, BRL, UYU and USD. Profitability views display amounts in your Dropi account currency. Cross-country auto-conversion is coming soon.",
      },
    ],
  },
};

export default function FAQ({ lang = "es", colors }) {
  const [openIdx, setOpenIdx] = useState(null);
  const data = FAQ_BY_LANG[lang] || FAQ_BY_LANG.es;
  const C = colors || { dark: "#0F172A", medium: "#475569", light: "#94A3B8", border: "#E5E7EB", green: "#059669", white: "#fff", bgLight: "#F8FAFC" };

  return (
    <section style={{ padding: "clamp(40px, 8vw, 90px) clamp(1rem, 7%, 7%)", background: C.bgLight }}>
      <div style={{ maxWidth: 760, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 48 }}>
          <h2 style={{ fontSize: "clamp(1.5rem, 3vw, 2.2rem)", fontWeight: 900, letterSpacing: "-0.03em", color: C.dark, margin: 0 }}>
            {data.title}
          </h2>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {data.items.map((item, i) => {
            const isOpen = openIdx === i;
            return (
              <div key={i} style={{
                background: C.white, border: `1px solid ${C.border}`,
                borderRadius: 14, overflow: "hidden",
                transition: "border-color 0.15s, box-shadow 0.15s",
                borderColor: isOpen ? C.green : C.border,
                boxShadow: isOpen ? "0 4px 16px rgba(5,150,105,0.08)" : "none",
              }}>
                <button
                  onClick={() => setOpenIdx(isOpen ? null : i)}
                  style={{
                    width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
                    gap: 16, padding: "18px 22px",
                    background: "transparent", border: "none", cursor: "pointer", fontFamily: "inherit",
                    textAlign: "left",
                  }}
                >
                  <span style={{ fontSize: 15, fontWeight: 700, color: C.dark, lineHeight: 1.4 }}>
                    {item.q}
                  </span>
                  <span style={{
                    color: isOpen ? C.green : C.light,
                    transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                    transition: "transform 0.2s ease, color 0.15s",
                    flexShrink: 0,
                  }}>
                    <ChevronDown size={20} strokeWidth={2.25} />
                  </span>
                </button>
                {isOpen && (
                  <div style={{
                    padding: "0 22px 20px",
                    fontSize: 14, color: C.medium, lineHeight: 1.7,
                  }}>
                    {item.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
